"use client";

import { memo, type ReactElement } from "react";
import Link from "next/link";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AspectRatio } from "@/components/ui/aspect-ratio";

import { FavoriteIconButton } from "./favorite";
import { ImageWithFallback } from "../property/image";
import { AdTypes } from "@/app/_components/ad-types";

import { formatBRL } from "@/helper/currency";
import { formatDateAuction, unixToDate } from "@/helper/date";
import { dateStyleCSS } from "@/helper/date";
import {
  formatPropertyDescriptionJsonLd,
  formatPropertyTitle,
  formatPropertyTitleUrl,
} from "@/helper/property";

import { banks } from "@/constants";

import type { House } from "@/types";
import { cn } from "@/lib/utils";

interface Props {
  estate: House;
  target?: HTMLAnchorElement["target"];
  footer?: ReactElement;
  revalidateMainListPath?: boolean;
}

export const ListItem = memo(
  ({ estate, target, revalidateMainListPath }: Props) => {
    if (estate.isAd) {
      return (
        <div className="group/item relative h-full">
          <Card
            className={`h-full w-full min-w-32 min-h-32 bg-slate-50 border-dashed border-2 border-slate-200 flex items-center justify-center overflow-hidden relative`}
          >
            <AdTypes adType={estate.adType || "google"} />
          </Card>
        </div>
      );
    }

    const title = formatPropertyTitle(estate);
    const alt = formatPropertyDescriptionJsonLd(estate);
    const mainImage = estate.imagens[0];

    return (
      <div className="group/item relative h-full">
        <FavoriteIconButton
          estate={estate}
          revalidateMainListPath={revalidateMainListPath}
        />
        <Link
          prefetch={false}
          target={target}
          className="appearance-none block p-0 text-left shadow-none border-none h-full"
          href={`/leilao-de-imoveis/${estate.uf}/imovel/${
            estate.numero_do_leilao
          }/${formatPropertyTitleUrl(estate)}`}
          scroll={false}
        >
          <Card className="h-full cursor-pointer hover:bg-gray-50 p-0 flex flex-col items-start justify-between gap-4 overflow-hidden relative">
            {estate.novo ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="absolute z-[1] left-0 top-0 bg-slate-100 px-2 py-0.5 rounded-br-lg">
                    <span className="flex items-center justify-center text-muted-foreground text-xs">
                      Novo
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Adicionado em{" "}
                    {unixToDate(new Date(estate.criado_em).getTime())}
                  </p>
                </TooltipContent>
              </Tooltip>
            ) : null}
            <div className="relative w-96">
              <AspectRatio ratio={16 / 9}>
                <ImageWithFallback
                  src={mainImage}
                  alt={alt}
                  className="h-full w-full object-cover object-center bg-muted-foreground"
                />
              </AspectRatio>
            </div>
            <CardHeader className="flex flex-col gap-0.5 items-start px-4 py-0 w-full space-y-0">
              <p className="text-sm text-muted-foreground">
                {estate.valor_de_avaliacao ? (
                  <>
                    <span className="line-through">
                      {formatBRL(estate.valor_de_avaliacao)}
                    </span>{" "}
                    <span
                      className={cn(
                        "text-sm",
                        estate.valor_lucro_liquido_com_corretor > 0
                          ? "text-green-600 font-medium"
                          : "text-red-600"
                      )}
                    >
                      {estate.desconto
                        ? `(${estate.desconto}% de desconto)`
                        : null}
                    </span>
                  </>
                ) : (
                  "Lance mínimo a definir"
                )}
              </p>
              <p
                className={cn(
                  estate.preco_atual ? "text-3xl font-semibold" : "text-2xl"
                )}
              >
                {estate.preco_atual
                  ? formatBRL(estate.preco_atual)
                  : "Valor de avaliação a definir"}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Lucro estimado:</span>{" "}
                {estate.preco_atual ? (
                  <span
                    className={cn(
                      "text-sm",
                      estate.valor_lucro_liquido_com_corretor > 0
                        ? "text-green-600"
                        : "text-red-600"
                    )}
                  >
                    <span className="font-semibold">
                      {formatBRL(estate.valor_lucro_liquido_com_corretor)}
                    </span>
                  </span>
                ) : (
                  <span>A definir</span>
                )}
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-0.5 items-start px-4 py-0 w-full space-y-0">
              <p className="text-sm font-semibold text-muted-foreground">
                {title}
              </p>
              <p className="text-xs text-muted-foreground">{estate.endereco}</p>
            </CardContent>
            <div className="px-4 flex flex-row gap-2 items-center">
              <img
                src={banks[estate.banco].logo}
                alt={`Imóvel em leilão - Banco ${estate.banco}`}
                width={53}
                height={53}
              />
              <p className="text-xs">
                <span className="text-muted-foreground">• Modalidade:</span>{" "}
                {estate.modalidade_de_venda}
              </p>
            </div>
            <CardFooter className="px-4 py-4 flex flex-col items-start gap-2 w-full space-y-0 border-t border-slate-200">
              {estate.data_de_licitacao2 && estate.data_de_licitacao1 ? (
                <div className="flex flex-col gap-2">
                  <p
                    className={`text-xs text-muted-foreground ${dateStyleCSS(
                      estate.data_de_licitacao1
                    )}`}
                  >
                    Data do 1° leilão:{" "}
                    {formatDateAuction(estate.data_de_licitacao1)} por{" "}
                    {formatBRL(estate.preco1)}
                  </p>
                  <p
                    className={`text-xs text-muted-foreground ${dateStyleCSS(
                      estate.data_de_licitacao2
                    )}`}
                  >
                    Data do 2° leilão:{" "}
                    {formatDateAuction(estate.data_de_licitacao2)} por{" "}
                    {formatBRL(estate.preco2)}
                  </p>
                </div>
              ) : (
                <p
                  className={`text-xs text-muted-foreground ${dateStyleCSS(
                    estate.data_de_licitacao1
                  )}`}
                >
                  Data do leilão: {formatDateAuction(estate.data_de_licitacao1)}
                </p>
              )}
            </CardFooter>
          </Card>
        </Link>
      </div>
    );
  }
);

ListItem.displayName = "ListItem";
